import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Building2,
  Check,
  CheckSquare,
  Copy,
  FileDown,
  Lightbulb,
  Mail,
  Plane,
  ShieldCheck,
  Sparkles,
  UserPlus,
  Users,
} from 'lucide-react';
import { useTutorial } from '../../contexts/TutorialContext';
import { TUTORIAL_TRACKS } from '../../constants/tutorialConstants';
import { listEmailAddresses } from '../../services/emailAddressService';

/** Standalone Getting Started guide for executive-assistant organizations. */

const SETUP_STEPS = [
  {
    icon: UserPlus,
    title: 'Invite your EA team',
    adminOnly: true,
    description:
      'Bring the assistants who coordinate travel into one shared workspace, with the right level of access for each person.',
    details: [
      'Go to Admin → Users & Invites and click "+ Invite User"',
      'Enter their email and keep the Executive Assistant role',
      'Check "Organization Admin" for anyone who should help manage the team',
      'Use Teams & Permissions when access should be limited to specific people or executives',
    ],
    cta: { label: 'Open Users & Invites', to: '/admin' },
  },
  {
    icon: Building2,
    title: 'Add your executives',
    description:
      'Create a profile for each executive so contact details, preferences, loyalty information, and upcoming travel stay organized in one place.',
    details: [
      'Go to Executives and click "Add Executive"',
      'Add their contact and company information',
      'Record travel preferences and loyalty details to make future planning faster',
    ],
    cta: { label: 'Add an Executive', to: '/executives?action=new-executive' },
  },
  {
    icon: Mail,
    title: 'Set up your confirmation inboxes',
    eachAssistant: true,
    description:
      'Forward booking confirmations instead of retyping them. Veyage parses the details and places reservations into your travel workflow.',
    details: [
      'Go to Utilities → Email Settings and click "Allocate Email Addresses"',
      'Use your standard inbox for reservations your team can work with',
      'Use your confidential inbox for sensitive travel visible only to authorized users',
      'Each assistant should allocate their own addresses',
    ],
    showParsingEmail: true,
    cta: { label: 'Open Email Settings', to: '/email-settings' },
  },
  {
    icon: Plane,
    title: 'Create the first trip',
    description:
      'A trip is the central workspace for an executive’s journey, bringing dates, travelers, reservations, documents, and tasks together.',
    details: [
      'Go to Trips and click "New Trip"',
      'Choose the executive and add a clear trip name and dates',
      'Open the trip to begin building its itinerary',
    ],
    cta: { label: 'Create a Trip', to: '/trips?action=new-trip' },
  },
  {
    icon: Mail,
    title: 'Build the itinerary',
    description:
      'Add flights, hotels, ground transportation, rail, and other details manually—or forward confirmations and let Veyage do the data entry.',
    details: [
      'Add travel components directly from the trip page, or',
      'Forward airline, hotel, rail, or car confirmations to a parsing address',
      'Review unfiled reservations and assign anything that could not be matched automatically',
    ],
    cta: { label: 'View Trips', to: '/trips' },
  },
  {
    icon: CheckSquare,
    title: 'Track tasks and share the plan',
    description:
      'Use todos to stay ahead of deadlines, then export a polished itinerary when the executive or another stakeholder needs the full plan.',
    details: [
      'Create todos for bookings, approvals, check-ins, and follow-ups',
      'Set due dates so important details do not slip through the cracks',
      'Download a trip PDF whenever you need a shareable or printable itinerary',
    ],
    cta: { label: 'Open Todos', to: '/todos' },
  },
];

const FEATURES = [
  {
    icon: Users,
    title: 'Executive profiles',
    description: 'Keep contact information, travel preferences, and loyalty details ready for every trip.',
  },
  {
    icon: Plane,
    title: 'Complete itineraries',
    description: 'Flights, hotels, rail, car service, and more in one chronological trip view.',
  },
  {
    icon: Mail,
    title: 'Email import',
    description: 'Forward confirmations and turn them into structured reservation details automatically.',
  },
  {
    icon: CheckSquare,
    title: 'Task management',
    description: 'Track bookings, deadlines, approvals, and follow-ups alongside the travel they support.',
  },
  {
    icon: FileDown,
    title: 'PDF itineraries',
    description: 'Export a polished itinerary for an executive, colleague, or travel-day backup.',
  },
  {
    icon: ShieldCheck,
    title: 'Controlled access',
    description: 'Use teams, permissions, and confidential inboxes to keep sensitive travel appropriately scoped.',
  },
];

const TIPS = [
  'Complete executive profiles before the first trip so repeat bookings take less time.',
  'Forward confirmations as soon as reservations are booked, then check Unfiled for anything that needs matching.',
  'Use consistent trip names and dates so teammates can find the right itinerary at a glance.',
];

export default function EATravelGuide() {
  const { startTutorial, tutorialEnabledForOrg } = useTutorial();
  const [parsingEmail, setParsingEmail] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    listEmailAddresses()
      .then((result) => {
        if (cancelled) return;
        const addresses = result?.inbound_email_addresses || result?.email_addresses || [];
        const general = addresses.find((address) => address.email_type === 'general');
        if (general?.email_address) setParsingEmail(general.email_address);
      })
      .catch(() => { /* The guide remains useful without an address. */ });
    return () => { cancelled = true; };
  }, []);

  const handleCopyParsingEmail = async () => {
    try {
      await navigator.clipboard.writeText(parsingEmail);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* Clipboard access is optional. */
    }
  };

  return (
    <div className="mx-auto max-w-5xl p-4 sm:p-6 lg:p-8">
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-700 via-blue-600 to-cyan-500 px-6 py-10 text-white sm:px-10 sm:py-14">
        <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-white/10" aria-hidden="true" />
        <div className="pointer-events-none absolute -bottom-24 -left-10 h-72 w-72 rounded-full bg-white/10" aria-hidden="true" />
        <div className="relative">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/20 px-3 py-1 text-xs font-semibold uppercase tracking-wide">
            <Sparkles className="h-3.5 w-3.5" /> Getting started guide
          </span>
          <h1 className="mt-4 text-3xl font-bold sm:text-4xl">Welcome to executive travel, simplified</h1>
          <p className="mt-3 max-w-2xl text-base text-blue-50 sm:text-lg">
            Set up your team, executives, and travel workflow in six practical steps—then manage every itinerary from one place.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/executives?action=new-executive"
              className="inline-flex items-center gap-2 rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-blue-700 shadow-sm hover:bg-blue-50"
            >
              <Building2 className="h-4 w-4" /> Add your first executive
            </Link>
            {tutorialEnabledForOrg && (
              <button
                onClick={() => startTutorial(TUTORIAL_TRACKS.EA)}
                className="inline-flex items-center gap-2 rounded-xl border border-white/40 px-5 py-2.5 text-sm font-semibold text-white hover:bg-white/10"
              >
                Take the interactive tour <ArrowRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="mt-12">
        <h2 className="text-2xl font-bold text-slate-800">Set up in six steps</h2>
        <p className="mt-1 text-slate-500">Follow these in order to build a reliable executive-travel workflow.</p>

        <ol className="mt-6 space-y-5">
          {SETUP_STEPS.map((step, index) => {
            const Icon = step.icon;
            return (
              <li key={step.title} className="relative rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
                <div className="flex flex-col gap-4 sm:flex-row sm:gap-6">
                  <div className="flex shrink-0 items-start gap-3 sm:flex-col sm:items-center">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-blue-700 to-cyan-500 text-sm font-bold text-white">
                      {index + 1}
                    </span>
                    <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
                      <Icon className="h-5 w-5" />
                    </span>
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-lg font-semibold text-slate-800">{step.title}</h3>
                      {step.adminOnly && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">
                          <ShieldCheck className="h-3 w-3" /> Admin
                        </span>
                      )}
                      {step.eachAssistant && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">
                          <Users className="h-3 w-3" /> Each assistant
                        </span>
                      )}
                    </div>
                    <p className="mt-1.5 text-sm text-slate-600">{step.description}</p>
                    <ul className="mt-3 space-y-1.5">
                      {step.details.map((detail) => (
                        <li key={detail} className="flex items-start gap-2 text-sm text-slate-500">
                          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-300" aria-hidden="true" />
                          {detail}
                        </li>
                      ))}
                    </ul>

                    {step.showParsingEmail && (
                      parsingEmail ? (
                        <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-violet-200 bg-violet-50 px-3.5 py-2.5">
                          <Mail className="h-4 w-4 shrink-0 text-violet-600" />
                          <span className="text-sm font-medium text-violet-900">Your organization's parsing address:</span>
                          <code className="break-all font-mono text-sm text-violet-800">{parsingEmail}</code>
                          <button
                            onClick={handleCopyParsingEmail}
                            title="Copy address"
                            aria-label="Copy parsing email address"
                            className="ml-auto rounded-lg p-1.5 text-violet-600 hover:bg-violet-100"
                          >
                            {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                          </button>
                        </div>
                      ) : (
                        <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm text-slate-600">
                          <Mail className="h-4 w-4 shrink-0 text-slate-400" />
                          Find your organization's parsing address under{' '}
                          <Link to="/email-settings" className="font-semibold text-blue-600 hover:text-blue-700">
                            Utilities → Email Settings
                          </Link>
                        </div>
                      )
                    )}

                    <Link to={step.cta.to} className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-700">
                      {step.cta.label} <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      </div>

      <div className="mt-14">
        <h2 className="text-2xl font-bold text-slate-800">Everything your team gets</h2>
        <p className="mt-1 text-slate-500">The tools executive assistants use throughout the travel lifecycle.</p>
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((feature) => {
            const Icon = feature.icon;
            return (
              <div key={feature.title} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-3 font-semibold text-slate-800">{feature.title}</h3>
                <p className="mt-1 text-sm text-slate-500">{feature.description}</p>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-14 rounded-2xl border border-amber-200 bg-amber-50 p-5 sm:p-6">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-amber-900">
          <Lightbulb className="h-5 w-5" /> Tips for a smooth start
        </h2>
        <ul className="mt-3 space-y-2">
          {TIPS.map((tip) => (
            <li key={tip} className="flex items-start gap-2 text-sm text-amber-800">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400" aria-hidden="true" />
              {tip}
            </li>
          ))}
        </ul>
      </div>

      <div className="mb-6 mt-14 rounded-3xl bg-slate-900 px-6 py-10 text-center text-white sm:px-10">
        <h2 className="text-2xl font-bold">Ready to organize your first trip?</h2>
        <p className="mx-auto mt-2 max-w-xl text-slate-300">
          Add an executive, create a trip, and forward the first confirmation. You can reopen this guide anytime from the sidebar.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Link to="/executives?action=new-executive" className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700">
            <Building2 className="h-4 w-4" /> Add an executive
          </Link>
          <Link to="/trips?action=new-trip" className="inline-flex items-center gap-2 rounded-xl border border-slate-600 px-5 py-2.5 text-sm font-semibold text-slate-200 hover:bg-slate-800">
            <Plane className="h-4 w-4" /> Create a trip
          </Link>
        </div>
      </div>
    </div>
  );
}
